import { ILogger } from '../..';
import { isRotate90Or270, isUndefined } from '../../utils';
import { Video2dContext, VideoGlContext } from '../video-context';
import { VideoNode, VideoNodeInfo } from './base';

// ref: https://developer.mozilla.org/zh-CN/docs/Web/API/CanvasRenderingContext2D/drawImage
interface CanvasDrawParams {
  sx: number;
  sy: number;
  sWidth: number;
  sHeight: number;
  dx: number;
  dy: number;
  dWidth: number;
  dHeight: number;
}
type fillMode = 'contain' | 'cover' | 'fill'
function calculateCanvasDrawParams(
  srcW: number,
  srcH: number,
  dstW: number,
  dstH: number,
  fit?: fillMode,
  swapWH = false
): CanvasDrawParams {
  // src 为源，dst 为目标画布，计算不同 fillMode 下把 src 绘制到 dst 上时 canvas.drawImage() 应该传入的参数值
  if (swapWH) [dstW, dstH] = [dstH, dstW]; // 前置节点旋转了90/270度，layout 也要先旋转再计算 fillMode
  const res = { sWidth: srcW, sHeight: srcH, dWidth: dstW, dHeight: dstH, sx: 0, sy: 0, dx: 0, dy: 0 };
  if (srcW === 0 || srcH === 0) return res;
  switch (fit) {
    case undefined: // 未指定 fillMode 或 'fill' 都是不裁剪源、直接拉伸到目标区域
    case 'fill':
      // 直接拉伸即可，使用原始尺寸和整个目标区域
      break;
    case 'contain': {
      // 保持比例完整显示在目标区域内
      const ratio = Math.min(dstW / srcW, dstH / srcH);
      res.dWidth = srcW * ratio;
      res.dHeight = srcH * ratio;
      // 计算居中位置
      res.dx = (dstW - res.dWidth) / 2;
      res.dy = (dstH - res.dHeight) / 2;
      break;
    }
    case 'cover': {
      // 保持比例覆盖整个目标区域（可能裁剪）
      const ratio = Math.max(dstW / srcW, dstH / srcH);
      // 计算源图上需要裁剪的尺寸
      const newSrcW = dstW / ratio;
      const newSrcH = dstH / ratio;
      // 居中裁剪
      res.sx = (srcW - newSrcW) / 2;
      res.sy = (srcH - newSrcH) / 2;
      // 使用裁剪后的源图尺寸
      res.sWidth = newSrcW;
      res.sHeight = newSrcH;
      break;
    }
  }
  return res;
}

export interface MixLayout {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  zIndex: number;
  fillMode?: fillMode;
  rotation?: 0 | 90 | 180 | 270
  hidden?: boolean
}
class NodeMixInfo {
  positionBuffer?: WebGLBuffer;
  // Cached draw params to avoid recalculation when input dimensions are unchanged
  private _cachedDrawParams: CanvasDrawParams | null = null;
  private _cachedDrawParamsKey = '';
  constructor(public node: VideoNode, public layout: MixLayout) {
  }
  /**
   * Get draw params with caching. Returns cached result if source/target dimensions and fillMode are unchanged.
   */
  getDrawParams(srcW: number, srcH: number, swapWH: boolean): CanvasDrawParams {
    const key = `${srcW},${srcH},${this.width},${this.height},${this.fillMode},${swapWH}`;
    if (this._cachedDrawParams && this._cachedDrawParamsKey === key) {
      return this._cachedDrawParams;
    }
    this._cachedDrawParams = calculateCanvasDrawParams(srcW, srcH, this.width, this.height, this.fillMode, swapWH);
    this._cachedDrawParamsKey = key;
    return this._cachedDrawParams;
  }
  invalidateDrawParamsCache() {
    this._cachedDrawParams = null;
    this._cachedDrawParamsKey = '';
  }
  get x() {
    return this.layout.x ?? this.node.x;
  }
  get y() {
    return this.layout.y ?? this.node.y;
  }
  get width() {
    return this.layout.width ?? this.node.width;
  }
  get height() {
    return this.layout.height ?? this.node.height;
  }
  get right() {
    return this.x + this.width;
  }
  get bottom() {
    return this.y + this.height;
  }
  get fillMode() {
    return this.layout.fillMode;
  }
  get rotation() {
    return this.layout.rotation;
  }
  get hidden() {
    return !!this.layout.hidden;
  }
}
export class VideoMixNode extends VideoNode<TexImageSource, Video2dContext> {
  inputs: NodeMixInfo[] = [];
  backgroundColor: string | CanvasGradient | CanvasPattern = 'black';
  constructor(context: Video2dContext, logger: ILogger) {
    super(context, {
      useDefaultProgram: true,
      useFbo: true,
      name: 'mix',
      create2d: true,
      logger
    });
  }
  addInput(node: VideoNode, layout: MixLayout) {
    // 二分查找合适的插入位置
    let low = 0; let high = this.inputs.length;
    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if (this.inputs[mid].layout.zIndex < layout.zIndex) {
        low = mid + 1;
      } else if (this.inputs[mid].layout.zIndex > layout.zIndex) {
        high = mid;
      } else {
        throw new Error(`input already exists at zIndex ${layout.zIndex}`);
      }
    }
    const info = new NodeMixInfo(node, layout);
    this.inputs.splice(low, 0, info);
  }
  changeInputLayout<S extends TexImageSource>(node: VideoNode<S>, layout: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    zIndex?: number;
    fillMode?: fillMode,
    rotation?: 0 | 90 | 180 | 270
    hidden?: boolean
  }) {
    const i = this.inputs.findIndex(input => input.node === node);
    if (i < 0) return;
    const { x, y, width, height, zIndex, fillMode, rotation, hidden } = layout;
    if (!isUndefined(zIndex) && this.inputs.some(input => input.layout.zIndex === zIndex && input.node !== node)) {
      throw new Error(`input already exists at zIndex ${layout.zIndex}`);
    }
    const input = this.inputs[i];
    if (!isUndefined(x)) input.layout.x = x;
    if (!isUndefined(y)) input.layout.y = y;
    if (!isUndefined(width)) input.layout.width = width;
    if (!isUndefined(height)) input.layout.height = height;
    if (!isUndefined(rotation)) input.layout.rotation = rotation;
    if (!isUndefined(hidden)) input.layout.hidden = hidden;
    if (fillMode) input.layout.fillMode = fillMode;
    if (!isUndefined(zIndex) && zIndex !== input.layout.zIndex) { // 更新了层级需要重新排序
      input.layout.zIndex = zIndex;
      this.inputs.sort((a, b) => a.layout.zIndex - b.layout.zIndex);
    }
  }
  hasInput<S extends TexImageSource>(node: VideoNode<S>): boolean {
    return this.inputs.some(input => input.node === node);
  }
  hasNoInput(): boolean {
    return this.inputs.length === 0;
  }
  resize(width: number, height: number): void {
    if (!this.matchInputSize) { // 根据入参固定宽高
      super.resize(width, height);
      return;
    }
    // 忽略入参自适应调整
    const size = this.inputs.reduce((size, input) => (input ? (Object.assign(size, { width: Math.max(size.width, input.right), height: Math.max(size.height, input.bottom) })) : size), { width: 0, height: 0 });
    super.resize(size.width, size.height);
    if (this.context instanceof VideoGlContext) {
      this.inputs.forEach(input => {
        if (input) {
          const data = this.layout2texCoords(input);
          if (input.positionBuffer) this.changeBufferData(input.positionBuffer!, data);
          else input.positionBuffer = this.createBuffer(data);
        }
      });
    }
  }
  connect<T extends VideoNode<TexImageSource>>(node: T, ...args: T extends VideoMixNode ? [MixLayout] : any[]): T {
    super.connect(node, ...args);
    this.drawBackGround2d(this.backgroundColor); // 初始化背景色，避免没有输入节点时画布背景色未绘制
    if (this.matchInputSize) this.resize(0, 0);
    return node;
  }
  removeInput<S extends TexImageSource>(node: VideoNode<S>) {
    this.inputs = this.inputs.filter(input => input.node !== node);
    if (this.inputs.length === 0) this.drawBackGround2d(this.backgroundColor); // 没有输入节点时，重新绘制背景色，避免残留帧
  }
  render(seq: number) {
    const gl = this.context.ctx as WebGL2RenderingContext;
    gl.clearColor(0, 0, 0, 0); // 设置清空颜色
    // gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT); // 清空颜色缓冲区和深度缓冲区
    const hasFrame = this.inputs.reduce((hasFrame, input) => (input.node.requestFrame(seq)) || hasFrame, false);
    if (hasFrame && gl) {
      this.useProgram();
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      this.useBufferFrame();
      for (let i = 0; i < this.inputs.length; i++) {
        const input = this.inputs[i];
        if (input) {
          // const layout = this.layouts.get(input)!;
          input.node.useTexture();
          this.draw(input.positionBuffer!);
        }
      }
      return true;
    }
    return false;
  }
  render2d(seq: number): boolean {
    // Skip requestFrame for hidden inputs to avoid unnecessary frame decoding overhead
    this.inputs.forEach(input => {
      if (!input.hidden) input.node.requestFrame(seq);
    });
    // 不再根据 hasFrame 来决定 mixNode 是否重新渲染，因为 layout 变化时需要绘制，但输入节点无法感知
    // 即输入节点 render 返回 false 时，mixNode 也始终重新渲染
    if (this.ctx2d) {
      this.drawBackGround2d(this.backgroundColor);
      for (let i = 0; i < this.inputs.length; i++) {
        const input = this.inputs[i];
        if (input.hidden) continue;
        // Use cached draw params to avoid recalculation when dimensions are unchanged
        const opt = input.getDrawParams(input.node.width, input.node.height, isRotate90Or270(input.rotation));
        // 注意为了兼容旧的 draw2d 接口，src 的参数放在后面
        // 裁剪源的参数直接传，目标画布坐标要加上自身的坐标
        this.draw2d(
          input.node.image, input.x + opt.dx, input.y + opt.dy, opt.dWidth, opt.dHeight,
          opt.sx, opt.sy, opt.sWidth, opt.sHeight
        );
        // this.debugLayout(input.x, input.y, input.width, input.height, isRotate90Or270(input.rotation));
      }
      return true;
    }
    return false;
  }
  debugLayout(dx: number, dy: number, dw: number, dh: number, swapWH = false) {
    // 绘制 layout 边框，直观显示 fillMode 效果
    if (this.ctx2d) {
      if (swapWH) [dw, dh] = [dh, dw];
      this.ctx2d.save();
      this.ctx2d.strokeStyle = 'red';
      this.ctx2d.lineWidth = 2;
      this.ctx2d.strokeRect(dx, dy, dw, dh);
      this.ctx2d.restore();
    }
  }
  getInfo(): { parent?: VideoNodeInfo | VideoNodeInfo[]; } & VideoNodeInfo {
    const { totalFrames, x, y, width, height, name } = this;
    const timestamp = Date.now();
    const fps = (totalFrames - this.lastInfo.totalFrames) / ((timestamp - this.lastInfo.timestamp) / 1000) >> 0;
    this.lastInfo = { totalFrames, x, y, width, height, timestamp, fps, name };
    return {
      parent: this.inputs.filter(input => input).map(input => input.node.getInfo()),
      ...this.lastInfo
    };
  }
  removeAllInputs() {
    this.inputs.forEach(input => {
      input.node.disconnect();
      if (input.positionBuffer && this.context instanceof VideoGlContext) {
        try {
          this.context.ctx?.deleteBuffer(input.positionBuffer);
        } catch {
        }
      }
    });
  }
  close() {
    super.close();
    this.removeAllInputs();
  }
}
